import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getDatabase, ref, get as getRTDB } from 'firebase/database';
import { getFirestore, doc, onSnapshot, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import app, { analytics, logEvent } from '../../services/firebase';


const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

/**
 * A custom sign-out function that logs the event to analytics
 * before signing the user out.
 */
export const signOutUser = async () => {
    const auth = getAuth(app);
    if (auth.currentUser) {
        logEvent(analytics, 'sign_out', {
            user_id: auth.currentUser.uid,
            user_email: auth.currentUser.email
        });
    }
    await signOut(auth);
};

export default function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // useEffect(() => {
    //     if (currentUser) {
    //         const auth = getAuth(app);
    //         console.log('Current UID:', currentUser.uid);
    //         console.log('Auth state:', auth.currentUser ? 'signed in' : 'not signed in');
    //     }
    // }, [currentUser]);

    useEffect(() => {
        const auth = getAuth(app);
        const rtdb = getDatabase(app);
        const db = getFirestore(app);
        let unsubscribeProfile = () => {};

        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            // Clean up old profile listener
            unsubscribeProfile();

            if (user) {
                const userProfileRef = doc(db, "users", user.uid);

                // Check if the user profile exists, and create it if it doesn't.
                // This is robust for social sign-ins or if the creation process was ever interrupted.
                try {
                    const docSnap = await getDoc(userProfileRef);
                    if (!docSnap.exists()) {
                        try {
                            const userProfile = {
                                email: user.email,
                                displayName: user.displayName || `User-${user.uid.substring(0, 5)}`,
                                userId: user.email.split('@')[0],
                                photoURL: user.photoURL || null, // from Auth
                                createdAt: serverTimestamp(),
                            };
                            logEvent(analytics, 'create_user_profile', {
                                email: userProfile.email,
                                source: 'on-the-fly'
                            });
                            await setDoc(userProfileRef, userProfile);
                        } catch (error) {
                            logEvent(analytics, 'create_user_profile_error', {
                                error_code: error.code,
                                error_message: error.message,
                            });
                        }
                    } else {
                        logEvent(analytics, 'user_profile_exists', {
                            email: docSnap.data().email
                        });
                    }
                } catch (error) {
                    logEvent(analytics, 'fetch_user_profile_error', {
                        error_message: error.message
                    });
                }

                // Now, set up the real-time listener.
                unsubscribeProfile = onSnapshot(userProfileRef, async (snapshot) => {
                    if (snapshot.exists()) {
                        const profileData = snapshot.data();

                        // Check for admin status in the Realtime Database
                        try {
                            const adminRef = ref(rtdb, `admins/${user.uid}`);
                            const adminSnapshot = await getRTDB(adminRef);
                            profileData.isAdmin = adminSnapshot.exists();
                            profileData.roles = profileData.roles || [];
                            if (profileData.isAdmin && !profileData.roles.includes('admin')) {
                                profileData.roles.push('admin');
                            }
                            // If you have /authors in RTDB:
                            const authorRef = ref(rtdb, `authors/${user.uid}`);
                            const authorSnap = await getRTDB(authorRef);
                            if (authorSnap.exists()) {
                            profileData.roles.push('author');
                            }
                        } catch (error) {
                            profileData.isAdmin = false; // Default to not admin on error
                            logEvent(analytics, 'admin_check_error', { error_message: error.message });
                        }
                        setUserProfile(profileData);
                        setLoading(false); // <-- MOVE LOADING STATE CHANGE HERE
                    } else {
                        logEvent(analytics, 'fetch_user_profile_warning', {
                            message: 'User profile does not exist in Firestore even after check.',
                            user_id: user.uid
                        });
                        setUserProfile(null);
                        setLoading(false); // Set loading false even if profile doesn't exist
                    }
                }, (error) => {
                    logEvent(analytics, 'firestore_listener_error', {
                        error_message: error.message
                    });
                    setUserProfile(null);
                    setLoading(false);
                });
            } else {
                // No user is signed in
                setUserProfile(null);
                setLoading(false);
            }
        });

        // Cleanup function for the main auth subscription
        return () => {
            unsubscribeAuth();
            unsubscribeProfile();
        };
    }, []);

    const value = {
        currentUser,
        userProfile,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

export { AuthContext };